'use client'
import { useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'
import { useAppDispatch, useAppSelector } from './hooks/redux-hook'
import { RootState } from '../(client)/redux/Store'
import LoadingPage from 'app/app/(client)/components/organisms/LoadingPage/loading'
import { useProgressInit } from './hooks/useAppInitProgress'
import { useParams, usePathname } from 'next/navigation'
import { OpportunitySfId } from 'app/types/types'
import { isValidUrl } from './utils/utils'
import { useDuplicateTabIdentifier } from './hooks/useDuplicateTabIdentifier'
import { isRtlLang } from 'rtl-detect'
import { AppSession } from '../api/auth/auth'
import { isEmpty } from 'ramda'
import { setOfferData } from './redux/features/currentOffer/current-offer-slice'
import { getOfferByOpportunityId } from './utils/opportunity-utils'
import BrowserNotSupported from './BrowserNotSupported'
import AlertBox from './components/organisms/AlertBox/AlertBox'
import { ProgressStatus, SessionStatus, StateStatus } from './utils/client-constants'
import { showAlert } from './redux/features/notification/alert-slice'
import { useNetwork } from './hooks/useNetwork'
import { syncOfferAsync } from './redux/features/sync/sync-offer-async'
import { updateTabAccess } from './redux/features/navigation/navigateToPage'

interface AppInitProps {
  readonly children?: React.ReactNode
}

const AppInit = ({ children }: AppInitProps) => {
  const dispatch = useAppDispatch()
  const { status: sessionStatus, data: sessionData } = useSession()
  const { status: masterDataStatus, data: masterData } = useAppSelector((state: RootState) => state.configdata)
  const { browserInfo, progressState, retry } = useProgressInit()
  const params = useParams()
  const pathname = usePathname()
  const isOnline = useNetwork()
  const [currentOpportunityId, setCurrentOpportunityId] = useState<OpportunitySfId | null>(null)
  const opportunityId = params?.opportunityId as OpportunitySfId | undefined
  useDuplicateTabIdentifier()
  const isValidPath = useMemo(() => isValidUrl(pathname), [pathname])
  const isHomePage = useMemo(() => pathname === '/', [pathname])
  const isAppInitialized = useAppSelector((state: RootState) => state.appInitialized.data)
  const currentOfferData = useAppSelector((state: RootState) => state.currentOffer.data)
  const unSavedOfferCount = useAppSelector(state => state.unSavedOfferCount).data || 0

  const appDetails = useMemo(() => {
    if (sessionStatus === SessionStatus.AUTHENTICATED && sessionData) {
      const {
        profile: { role, display_name, salesOrganization }
      } = sessionData as AppSession
      return { role, displayName: display_name, salesOrganization }
    }
    return null
  }, [sessionStatus, sessionData])

  useEffect(() => {
    if (isAppInitialized && isHomePage && isOnline && unSavedOfferCount > 0) {
      dispatch(syncOfferAsync())
    }
  }, [isHomePage, isOnline, unSavedOfferCount, dispatch, isAppInitialized])

  useEffect(() => {
    if (masterDataStatus === StateStatus.SUCCEEDED) {
      const language = masterData?.settings?.language
      document.body.setAttribute('dir', language && isRtlLang(language) ? 'rtl' : 'ltr')
    }
  }, [masterDataStatus, masterData?.settings?.language])

  useEffect(() => {
    const isOfferAlreadyLoaded = currentOfferData && currentOfferData.opportunityId === opportunityId
    const shouldLoadOffer =
      opportunityId && !isHomePage && opportunityId !== currentOpportunityId && !isOfferAlreadyLoaded
    if (isAppInitialized && shouldLoadOffer && opportunityId) {
      getOfferByOpportunityId(opportunityId)
        .then(offer => {
          if (offer) {
            dispatch(setOfferData(offer))
            dispatch(updateTabAccess(offer))
            setCurrentOpportunityId(opportunityId)
          }
        })
        .catch(error => {
          dispatch(
            showAlert({
              title: 'Error loading offer data.',
              description: error,
              type: 'error',
              id: ''
            })
          )
        })
    } else if (isOfferAlreadyLoaded && opportunityId !== currentOpportunityId) {
      setCurrentOpportunityId(opportunityId)
    }
  }, [isAppInitialized, opportunityId, isHomePage, currentOpportunityId, currentOfferData, dispatch])

  const appName = useMemo(() => {
    if (!appDetails || isEmpty(appDetails)) {
      return ''
    }
    return `${appDetails.displayName} (${appDetails.role}, ${appDetails.salesOrganization})`
  }, [appDetails])

  if (browserInfo && !browserInfo.isCompatible) {
    return <BrowserNotSupported {...browserInfo} />
  }

  if (!isAppInitialized && isValidPath && progressState) {
    return (
      <>
        <div className="h-screen flex flex-col items-center justify-center gap-4">
          {pathname !== '/logout' ? (
            <LoadingPage
              percentage={progressState.value}
              label={progressState.message}
              variant={progressState.variant || ProgressStatus.LOADING}
              appName={appName}
              errorMessage={progressState.errorMessage || ''}
              errorCode={progressState.errorCode}
              errorStack={progressState.errorStack}
              onRetry={retry}
            />
          ) : null}
        </div>
        <AlertBox />
      </>
    )
  }
  return children
}
export default AppInit
